import { acceptHMRUpdate, defineStore } from 'pinia';

import { ApplicationWithRenterDto } from '@tallo/applications';
import { useAlertsStore } from '@tallo/design-system';
import { ApplicationBundleDto, applicationsService } from '@tallo/investor/applications';
import { ConversationDto } from '@tallo/investor/conversation';
import { PropertyInquiryDto, propertyInquiryService } from '@tallo/investor/property';
import { renterProfileService } from '@tallo/investor/renter';
import { RenterDto } from '@tallo/renter';

export const useRenterProfileStore = defineStore({
  id: 'renterProfile',
  state: (): {
    profile: {
      isLoaded: boolean;
      profile: RenterDto | null;
    };
    conversation: {
      isLoaded: boolean;
      conversation: ConversationDto | null;
    };
    inquiries: {
      isLoaded: boolean;
      inquiries: PropertyInquiryDto[] | null;
      activeInquiry: PropertyInquiryDto | null;
    };
    bundles: {
      isLoaded: boolean;
      bundleOperationInProgress: boolean;
      bundles: ApplicationBundleDto[];
    };
    applications: {
      isLoaded: boolean;
      selected: ApplicationWithRenterDto[];
    };
  } => ({
    profile: {
      isLoaded: false,
      profile: null,
    },
    conversation: {
      isLoaded: false,
      conversation: null,
    },
    inquiries: {
      isLoaded: false,
      inquiries: null,
      activeInquiry: null,
    },
    bundles: {
      isLoaded: false,
      bundleOperationInProgress: false,
      bundles: [],
    },
    applications: {
      isLoaded: false,
      selected: [],
    },
  }),
  getters: {
    getRenterProfile: (state) => state.profile.profile,
    getIsProfileLoaded: (state) => state.profile.isLoaded,
    getIsConversationLoaded: (state) => state.conversation.isLoaded,
    getActiveConversation: (state) => state.conversation,
    getIsInquiriesLoaded: (state) => state.inquiries.isLoaded,
    getInquiries: (state) => state.inquiries.inquiries,
    getActiveInquiry: (state) => state.inquiries.activeInquiry,
    getApplicationBundles: (state) => state.bundles.bundles,
    hasApplicationBundles: (state) => state.bundles.bundles?.length > 0,
    isApplicationSent: (state) => {
      return (propertyId: string) => {
        return state.bundles.bundles.find(({ property }) => property.id === propertyId);
      };
    },
  },
  actions: {
    load(renterId: string) {
      this.loadProfileById(renterId);
      this.loadInquiries(renterId);
      this.loadBundles(renterId);
    },

    async loadProfileById(renterId: string): Promise<void> {
      this.profile.isLoaded = false;
      this.profile.profile = await renterProfileService.getProfileById(renterId);
      this.profile.isLoaded = true;
    },

    async loadProfileByEmail(email: string): Promise<void> {
      this.profile.isLoaded = false;
      this.profile.profile = await renterProfileService.getProfileByEmail(email);
      this.profile.isLoaded = true;
    },

    async loadConversation(inquiryId: string): Promise<void> {
      this.conversation.isLoaded = false;
      this.conversation.conversation = await propertyInquiryService.getInquiryConversation(inquiryId);
      this.conversation.isLoaded = true;
    },

    async loadInquiries(renterId: string): Promise<void> {
      this.inquiries.isLoaded = false;
      this.inquiries.inquiries = await propertyInquiryService.getAllInquiriesByRenter(renterId);
      this.inquiries.isLoaded = true;
    },

    async setActiveInquiry(inquiryId: string): Promise<void> {
      this.inquiries.activeInquiry =
        this.inquiries.inquiries?.find((inquiry: PropertyInquiryDto) => inquiry.id === inquiryId) || null;
    },

    async loadBundles(renterId: string): Promise<void> {
      this.bundles.isLoaded = false;
      this.bundles.bundles = await applicationsService.getBundlesByRenterId(renterId);
      this.bundles.isLoaded = true;
    },

    async loadApplicationsByBundleId(id: string): Promise<void> {
      this.applications.selected = [];
      this.applications.isLoaded = false;

      const applications = await applicationsService.getApplicationsByBundleId(id);
      this.applications.selected = [...applications]
        .filter((application) => !!application)
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

      this.applications.isLoaded = true;
    },

    async sendApplication(inquiryId: string): Promise<void> {
      await applicationsService.send(inquiryId);
      await this.loadBundles(this.profile.profile?.id!);
    },

    async resendApplication(applicationBundleId: string): Promise<void> {
      await applicationsService.resend(applicationBundleId);
      await this.loadBundles(this.profile.profile?.id!);
    },

    async acceptBundle(applicationBundleId: string): Promise<void> {
      const alerts = useAlertsStore();

      this.bundles.bundleOperationInProgress = true;

      try {
        const acceptedBundle = await applicationsService.accept(applicationBundleId);
        this.putBundle(acceptedBundle);
      } catch (error) {
        alerts.somethingWentWrong();
      } finally {
        this.bundles.bundleOperationInProgress = false;
      }
    },

    async declineBundle(applicationBundleId: string): Promise<void> {
      const alerts = useAlertsStore();

      this.bundles.bundleOperationInProgress = true;

      try {
        const declinedBundle = await applicationsService.decline(applicationBundleId);
        this.putBundle(declinedBundle);
      } catch (error) {
        alerts.somethingWentWrong();
      } finally {
        this.bundles.bundleOperationInProgress = false;
      }
    },

    putBundle(bundle: ApplicationBundleDto): void {
      const index = this.bundles.bundles.findIndex(({ id }) => id === bundle.id);

      if (index > -1) {
        this.bundles.bundles[index] = bundle;
      } else {
        this.bundles.bundles.push(bundle);
      }
    },
  },
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useRenterProfileStore, import.meta.hot));
}
